import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { SortDirection } from 'react-virtualized';
// FSD cell renderers (lifted out of `_legacy/components/Shared/Table/
// widgets/Cell/`). Kept as default + named exports so the existing
// call sites stay one-liners.
import { CustomTableCell, CellSimpleText } from '../cells';
import { checkPermission } from '@shared/lib/checkPermissions';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  getLocalizedAddress,
  getLocalizedDate,
  getLocalizedLabel,
  getLocalizedDateWithTime,
} from '@shared/lib/localize';
import { RESOURCE_STATUSES_OPTIONS } from '@entities/resource';
import {
  CUSTOM_TAGS_OPTIONS,
  PATIENT_CUSTOM_TAGS_OPTIONS,
  SURVEY_TAGS_OPTIONS,
} from '_legacy/components/Shared/CustomTags';
import { ALERTS_TYPE_SELECTOR_OPTIONS, STATUS_OPTIONS, ALERT_TAGS_OPTIONS } from '@entities/alert';
import {
  BILLING_CLAIMS_STATUSES_OPTIONS,
  CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
  CLAIM_STATUS_SELECTOR_OPTIONS,
  ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
} from '@entities/claim';
import { ASSESSMENT_STATUSES_OPTIONS, NO_SHOW_STATUSES_OPTIONS } from '@entities/assessment';
import { FORMULA_OPTIONS, ORDER_STATUS_OPTIONS, ORDER_TYPES } from '@entities/order';
// Imported from the module rather than the entity barrel on purpose:
// the barrel also pulls in the assessment columns and the permission
// helper, and the latter reaches `@app/router`. Importing it from here,
// inside the shared Table, made that a cycle and the binding read as
// `undefined` at module-eval time. This file has only an i18next dep.
import { CREATION_METHOD_OPTIONS } from '@entities/assessment/model/creationMethods';
import { CLAIM_REPORTS_STATUSES_OPTIONS } from '@shared/lib/claimReportStatuses';
import FILTER_TYPES from '../Filters/constants';
import FilterBox from '../Filters/FilterBox';
import cx from 'classnames';
import { BOT_UPLOADS_STATUSES_OPTIONS, DOCUMENT_TYPES_OPTIONS } from '@entities/bot-activity';
import { BHI_STATUS_OPTIONS } from '@entities/program';
import { TABLE_KEYS } from 'redux_store';
import { GENDER_OPTIONS } from '@entities/population-health';
import { TEXT_FILTERS, registerTextFilter } from '@shared/config/textFilters';

export { TEXT_FILTERS };

// Single canvas reused across calls. The legacy version created a
// fresh `<canvas>` per column per `updateColumns` call (12+ columns,
// run on every fetch / sort / filter), churning DOM allocations that
// the GC had to mop up.
let _textMeasureCanvas = null;
const _textWidthCache = new Map();

const getTextWidth = (text, font = '14px Roboto, Helvetica, Arial, sans-serif') => {
  if (!text || typeof text !== 'string') return 0;
  const cacheKey = `${font}::${text}`;
  const hit = _textWidthCache.get(cacheKey);
  if (hit !== undefined) return hit;

  if (!_textMeasureCanvas) {
    _textMeasureCanvas = document.createElement('canvas');
  }
  const context = _textMeasureCanvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  const width = Math.ceil(metrics.width + 20);
  _textWidthCache.set(cacheKey, width);
  return width;
};

export const FILTERING_RULES = {
  exact: 'exact',
  startWith: 'startWith',
  include: 'include',
};

export const SORTING_TYPES = {
  string: 'string',
  number: 'number',
};

function randomIntFromInterval(min, max) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export const getFilteredData = (data, filters, filtersRulesByKey = {}) => {
  const noData = !data || data.length === 0;
  const noFilters =
    filters.reduce((accumulator, { value: currentValue }) => {
      if (Array.isArray(currentValue)) {
        return accumulator + currentValue.join('').trim().length;
      } else {
        return accumulator + currentValue.trim().length;
      }
    }, 0) === 0;

  if (noData) {
    return [];
  } else if (noFilters) {
    return data;
  } else {
    return data.filter(item => {
      let res = true;
      filters.forEach(({ id, value }) => {
        if (res) {
          const currentValue = item[id];
          if (!!value) {
            const filterAsArray = Array.isArray(value) ? value : value.split(',');

            if (filterAsArray.length > 1) {
              res = filterAsArray
                .map(item =>
                  _checkIsMatch(
                    currentValue,
                    item,
                    filtersRulesByKey[id] || { type: FILTERING_RULES.exact }
                  )
                )
                .some(x => x);
            } else {
              res = _checkIsMatch(
                currentValue,
                value,
                filtersRulesByKey[id] || { type: FILTERING_RULES.include }
              );
            }
          }
        }
      });
      return res;
    });
  }
};

const _checkIsMatch = (value, filterValue, filterRules = {}) => {
  if (!filterValue) {
    return true;
  }
  const { type, postfix = '' } = filterRules;
  const test = value.toString().toLowerCase();
  const _filterValue = filterValue.toString().toLowerCase();

  switch (type) {
    case FILTERING_RULES.include: {
      return test.includes(_filterValue);
    }
    case FILTERING_RULES.exact: {
      return value.toString() === filterValue || value === filterValue + postfix;
    }
    case FILTERING_RULES.startWith: {
      return test.startsWith(_filterValue);
    }
  }
};

export const getSortedData = (
  data,
  sort,
  sortingRules = {
    type: SORTING_TYPES.string,
  }
) => {
  if (!data || data.length === 0) {
    return [];
  } else if (!sort || !sort.id) {
    return data;
  }
  const { id: sortBy, desc } = sort;
  const sortDirection = desc ? SortDirection.ASC : SortDirection.DESC;
  const { type } = sortingRules[sortBy];
  return data.sort((a, b) => {
    if (sortDirection === SortDirection.ASC) {
      if (type === SORTING_TYPES.string) {
        return a[sortBy].toString() > b[sortBy].toString();
      } else if (type === SORTING_TYPES.number) {
        return a[sortBy] - b[sortBy];
      }
    } else {
      if (type === SORTING_TYPES.string) {
        return a[sortBy].toString() < b[sortBy].toString();
      } else if (type === SORTING_TYPES.number) {
        return b[sortBy] - a[sortBy];
      }
    }
  });
};

export const currencyFormat = value => {
  return '$ ' + (value ? Number(value) : 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

/**
 * Post-processing for useGenerateColumns output: attach LIVE
 * `staticFilters` context entries to one column's dynamic-hint
 * filter. useGenerateColumns freezes column defs at mount (it only
 * rebuilds when `args`/`additionalLoadsResults` change), so context
 * that follows dashboard state — a date range, a CPT selection —
 * must be injected into the generated columns on every change, not
 * baked into the defs. Mirrors the pattern used for the assessments
 * table's Instant Created option stripping.
 */
export const injectFilterStaticFilters = (columns, accessorKey, staticFilters) =>
  columns.map(column => {
    if (column.accessorKey !== accessorKey || !column.meta?.qhsFilter) return column;
    return {
      ...column,
      meta: {
        ...column.meta,
        qhsFilter: {
          ...column.meta.qhsFilter,
          ...(staticFilters?.length ? { staticFilters } : { staticFilters: undefined }),
        },
      },
    };
  });

export const useGenerateColumns = (columnsData, tableKey, args, additionalLoadsResults) => {
  const {
    metadata: { currentAccountPermissions },
  } = useSelector(state => state.user);
  const { settings } = useSelector(state => state.table);
  const columnSizes = settings[tableKey] ? settings[tableKey].columnSizes ?? 200 : 200;
  const columnVisibility = settings[tableKey] ? settings[tableKey].columnVisibility ?? {} : {};
  const firstVisibleColumKey = Object.keys(columnVisibility).find(key => columnVisibility[key]);
  const prevRef = useRef();
  const [columns, setColumns] = useState([]);

  const updateColumns = () => {
    const isEqual = JSON.stringify(prevRef.current) === JSON.stringify(additionalLoadsResults);
    if (!isEqual) {
      if (additionalLoadsResults) {
        const columns = Array.isArray(columnsData)
          ? columnsData
          : columnsData(...(args ? Object.values(args) : []));
        setColumns(
          columns
            .filter(item => {
              const hasPermission = item.permission
                ? checkPermission(currentAccountPermissions, item.permission)
                : true;
              return item.hidden !== true && hasPermission;
            })
            .map(
              ({
                header,
                accessorKey,
                permission,
                hidden,
                filter,
                cell,
                enableHiding,
                disableSorting,
                Edit,
                isDefaultCell,
                includeCurrentAccount,
                customAccountUuidForFilter,
              }) => {
                const headerTextWidth = getTextWidth(header);
                const calculatedMinSize = Math.max(100, headerTextWidth + 80);

                let qhsFilter = null;
                if (filter) {
                  if (!filter.filterType || filter.filterType === FILTER_TYPES.PRICE) {
                    registerTextFilter(accessorKey);
                  }
                  const options =
                    additionalLoadsResults && accessorKey in additionalLoadsResults
                      ? additionalLoadsResults[accessorKey]
                      : filter.options;
                  qhsFilter = {
                    ...filter,
                    options,
                    isFilterable: true,
                    withBubble: cell?.withBubble,
                    includeCurrentAccount,
                    customAccountUuidForFilter,
                  };
                }

                return {
                  size: columnSizes[accessorKey],
                  header,
                  accessorKey,
                  permission,
                  hidden,
                  filter,
                  Edit,
                  minSize: calculatedMinSize,
                  maxSize: 500,
                  enableHiding: enableHiding === false ? false : true,
                  enableSorting: !disableSorting,
                  Cell: QhsCellRenderer,
                  Filter: filter ? QhsFilterRenderer : _emptyFilter,
                  meta: {
                    qhs: {
                      accessorKey,
                      isDefaultCell,
                      formattedFunction: cell?.formattedFunction,
                      infoPanePage: cell?.infoPanePage,
                      withBubble: cell?.withBubble,
                      withBubbles: cell?.withBubbles,
                      // Second, muted line under the value, read from
                      // another row field (a program's description under
                      // its name). One line, ellipsis, full text on hover.
                      subtextKey: cell?.subtextKey,
                      selectorOptionsFromApi:
                        additionalLoadsResults && additionalLoadsResults[accessorKey],
                      firstVisibleColumKey,
                    },
                    qhsFilter,
                  },
                };
              }
            )
        );
      }
      prevRef.current = additionalLoadsResults;
    }
  };

  useEffect(() => {
    updateColumns();
  }, [...(args ? Object.values(args) : []), additionalLoadsResults]);

  useEffect(() => {
    updateColumns();
  }, []);

  return { columns };
};

export const convertDataFromApi = (
  data,
  tableKey,
  filters,
  additionalKeys,
  additionalLoadsResults
) => {
  return [...(data || [])].map(item => {
    return _convertApiToTableDataValues(
      { ...item, ...additionalKeys },
      tableKey,
      filters,
      additionalLoadsResults
    );
  });
};

const _convertApiToTableDataValues = (obj, tableKey, filters, additionalLoadsResults) => {
  let result = { ...obj };

  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'function') {
      if (obj[key](obj) === '') {
        result[key] = '-';
      } else {
        result[key] = obj[key](obj);
      }
      return;
    }

    switch (key) {
      case 'createdTimestamp': {
        if (tableKey === TABLE_KEYS.alertsDSTable || tableKey === TABLE_KEYS.alertsTable) {
          result[key] = obj[key] ? getLocalizedDateWithTime(obj[key]) : '-';
        } else {
          result[key] = obj[key] ? getLocalizedDate(obj[key], 'UTC') : '-';
        }
        break;
      }
      case 'patientDateOfBirth':
      case 'dateOfBirth': {
        const dateValue = obj[key];
        result[key] =
          dateValue && !dateValue.startsWith('0001-01-01')
            ? getLocalizedDate(dateValue, 'UTC')
            : '-';
        break;
      }
      case 'appointmentTimestamp':
      case 'invitedTimestamp':
      case 'viewedTimestamp':
      case 'engagedTimestamp':
      case 'acknowledgedTimestamp':
      case 'claimGeneratedTimestamp':
      case 'lastModifiedTimestamp':
      case 'entityCompletedTimestamp':
      case 'paymentDate':
      case 'entityOrderTimestamp':
      case 'orderDate':
      case 'reportableGeneratedTimestamp':
      case 'claimReportGeneratedTimestamp':
      case 'dos':
      case 'documentUploadTimestamp':
      case 'uploadTimestamp':
      case 'programDocDocumentUploadTimestamp':
      case 'orderTimestamp': {
        result[key] = obj[key] ? getLocalizedDate(obj[key], 'UTC') : '-';
        break;
      }
      case 'totalAmount':
      case 'cptCount':
      case 'allowed':
      case 'cptPaid':
      case 'price':
      case 'pr':
      case 'paid':
      case 'totalCPTs': {
        result[key] = obj[key] ? obj[key].toString() : '0';
        break;
      }
      case 'mrnNumber':
      case 'patientMrnNumber':
      case 'patientLastName':
      case 'patientFirstName': {
        result[key] = obj[key] ? obj[key] : '-';
        break;
      }
      case 'address': {
        result.address = obj.address ? getLocalizedAddress(obj.address) : '-';
        break;
      }

      case 'orderType':
      case 'alertType':
      case 'entityType':
      case 'acknowledgeStatus':
      case 'encounterStatus':
      case 'billingStatus':
      case 'claimStatus':
      case 'orderStatus':
      case 'orderFormula':
      case 'botUploadStatus':
      // Shares the bot-upload status vocabulary with `botUploadStatus`;
      // without a case here it rendered the raw API string ("success")
      // next to a sibling column that showed "Success", and carried no
      // `_rawValues` entry for the badge tone to read.
      case 'programDocUploadStatus':
      case 'documentStatus':
      case 'documentType':
      case 'patientGender':
      case 'bhiStatus':
      case 'reportStatus': {
        const options = OPTIONS_BY_KEY[key];
        if (obj[key]) {
          if (!result._rawValues) result._rawValues = {};
          result._rawValues[key] = obj[key];
        }
        result[key] = obj[key] ? getLocalizedLabel(options, obj[key], key === 'alertType') : '-';
        break;
      }
      case 'surveyTags': {
        const options = OPTIONS_BY_KEY[key];
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        if (obj[key]) {
          const sorted = obj[key].sort(function (a, b) {
            const _a = filter.includes(a) ? 1 : 0;
            const _b = filter.includes(b) ? 1 : 0;
            return _b - _a;
          });
          if (!result._rawValues) result._rawValues = {};
          result._rawValues[key] = [...sorted];
          result[key] = sorted.map(item => {
            return getLocalizedLabel(options, item, null, null, true);
          });
        } else {
          result[key] = '-';
        }
        break;
      }
      case 'accountCustomTags':
      case 'patientCustomTags':
      case 'customTags': {
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        const isPatientOptions =
          key === 'patientCustomTags' ||
          (key === 'customTags' &&
            (tableKey === TABLE_KEYS.patientsDSTable || tableKey === TABLE_KEYS.patientsTable));
        if (obj[key]) {
          const sorted = obj[key].sort(function (a, b) {
            const _a = filter.includes(a) ? 1 : 0;
            const _b = filter.includes(b) ? 1 : 0;
            return _b - _a;
          });
          if (!result._rawValues) result._rawValues = {};
          result._rawValues[key] = [...sorted];
          result[key] = sorted.map(item => {
            return getLocalizedLabel(
              isPatientOptions ? PATIENT_CUSTOM_TAGS_OPTIONS : CUSTOM_TAGS_OPTIONS,
              item
            );
          });
        } else {
          result[key] = '-';
        }
        break;
      }
      case 'status': {
        if (obj.status) {
          if (!result._rawValues) result._rawValues = {};
          result._rawValues[key] = obj.status;
        }
        if (tableKey === TABLE_KEYS.resourcesDSTable || tableKey === TABLE_KEYS.resourcesTable) {
          result.status = getLocalizedLabel(RESOURCE_STATUSES_OPTIONS, obj.status) || '-';
        }
        if (
          tableKey === TABLE_KEYS.assessmentsDSTable ||
          tableKey === TABLE_KEYS.assessmentsTable ||
          tableKey === TABLE_KEYS.botActivitiesDSTable ||
          tableKey === TABLE_KEYS.botActivitiesTable
        ) {
          result.status = getLocalizedLabel(ASSESSMENT_STATUSES_OPTIONS, obj.status) || '-';
        }
        break;
      }
      case 'noShowStatus': {
        if (obj.noShowStatus) {
          if (!result._rawValues) result._rawValues = {};
          result._rawValues[key] = obj.noShowStatus;
        }
        if (
          tableKey === TABLE_KEYS.assessmentsDSTable ||
          tableKey === TABLE_KEYS.assessmentsTable
        ) {
          result.noShowStatus =
            getLocalizedLabel(NO_SHOW_STATUSES_OPTIONS, obj.noShowStatus) || '-';
        }
        break;
      }
      case 'title': {
        if (tableKey === TABLE_KEYS.resourcesDSTable || tableKey === TABLE_KEYS.resourcesTable) {
          result.resource = obj.title ? obj.title : '-';
        }
        break;
      }
      case 'accountCustomIdentifier':
      case 'customIdentifier': {
        result[key] = obj[key] ? obj[key] : 'N/A';
        break;
      }
      case 'claimable': {
        result.claimable = obj.result === false;
        break;
      }
      case 'createdBy': {
        // Was a nested ternary over three hardcoded English labels; the
        // API's CreationMethod has four values, so `instant` fell
        // through and rendered as the raw string. Now a lookup over the
        // entity's own list, translated like every other label.
        if (obj.createdBy) {
          if (!result._rawValues) result._rawValues = {};
          result._rawValues.createdBy = obj.createdBy;
        }
        result.createdBy =
          CREATION_METHOD_OPTIONS.find(option => option.value === obj.createdBy)?.label ||
          obj.createdBy ||
          '-';
        break;
      }
      case 'resourceName': {
        result.resourceName = {
          resourceName: obj.resourceName || '-',
          resourceType: obj.resourceType || '-',
        };
        break;
      }
      case 'alertTags': {
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        result.alertTags = obj.alertTags
          ? obj.alertTags
              .sort(function (a, b) {
                const _a = filter
                  .map(item => item.replace(' ', '').toLowerCase())
                  .includes(a.replace(' ', '').toLowerCase())
                  ? 1
                  : 0;
                const _b = filter
                  .map(item => item.replace(' ', '').toLowerCase())
                  .includes(b.replace(' ', '').toLowerCase())
                  ? 1
                  : 0;
                return _b - _a;
              })
              .map(item => {
                let alertTag = item;
                if (
                  alertTag === 'SUICIDE OR SELF HARM' ||
                  alertTag === 'SUICIDE OF SELF HARM' ||
                  alertTag === 'Suicide or Self Harm Risk' ||
                  alertTag === 'Suicide of Self Harm' ||
                  alertTag === 'Suicide or Self Harm-Risk'
                ) {
                  alertTag = 'Suicide or Self-Harm Risk';
                }
                const backendOptions =
                  additionalLoadsResults && additionalLoadsResults[key]
                    ? additionalLoadsResults[key]
                    : [];
                // Merge backend-supplied options with the frontend
                // fallback list so newly-added alert tags (e.g.
                // PHQ-GAD16 / Q-Lung taxonomy) render with proper
                // labels even before /accounts/alerts/tags returns
                // them. Backend entries win on collision; static
                // entries fill the gap.
                const seenValues = new Set(backendOptions.map(o => o?.value));
                const mergedOptions = [
                  ...backendOptions,
                  ...ALERT_TAGS_OPTIONS.filter(o => !seenValues.has(o.value)),
                ];
                // A tag the options do not know yet is still the text
                // the API chose for it, so it is shown as is rather than
                // as "Invalid Data" (QA 2026-10-30, Q-Nutrition tags).
                return getLocalizedLabel(mergedOptions, alertTag, true, '@@@', true) || '-';
              })
          : [];

        break;
      }
    }
  });

  return result;
};

const OPTIONS_BY_KEY = {
  patientCustomTags: PATIENT_CUSTOM_TAGS_OPTIONS,
  accountCustomTags: CUSTOM_TAGS_OPTIONS,
  entityType: CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
  alertType: ALERTS_TYPE_SELECTOR_OPTIONS,
  acknowledgeStatus: STATUS_OPTIONS,
  surveyTags: SURVEY_TAGS_OPTIONS,
  reportStatus: CLAIM_REPORTS_STATUSES_OPTIONS,
  orderFormula: FORMULA_OPTIONS,
  orderType: ORDER_TYPES,
  orderStatus: ORDER_STATUS_OPTIONS,
  claimStatus: CLAIM_STATUS_SELECTOR_OPTIONS,
  billingStatus: BILLING_CLAIMS_STATUSES_OPTIONS,
  encounterStatus: ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
  botUploadStatus: BOT_UPLOADS_STATUSES_OPTIONS,
  programDocUploadStatus: BOT_UPLOADS_STATUSES_OPTIONS,
  documentStatus: BOT_UPLOADS_STATUSES_OPTIONS,
  documentType: DOCUMENT_TYPES_OPTIONS,
  patientGender: GENDER_OPTIONS,
  bhiStatus: BHI_STATUS_OPTIONS,
};

// `QhsCellRenderer` / `QhsFilterRenderer` — module-level stable
// components that MRT-style consumers reference by stable identity.
// Per-column configuration lives in `column.columnDef.meta.qhs` and
// is read here, so swapping `Cell: QhsCellRenderer` keeps the same
// reference across `updateColumns` calls. The legacy version inlined
// `const CustomCell = props => ...` inside `.map()`, which produced
// a fresh component reference per column per call — MRT then treated
// every column as "changed" and remounted the whole body on every
// fetch / sort / filter / settings update.
const _emptyFilter = () => null;

// Plain function (NOT memo-wrapped) — Mantine React Table invokes
// `columnDef.Cell(...)` as a function call site, not as a JSX
// element. Wrapping in `React.memo` produces an object MRT can't
// invoke ("columnDef.Cell is not a function"). The inner `<Cell>`
// component below is memoed; that's where row-level memoization
// happens.
function QhsCellRenderer(props) {
  const meta = props.column?.columnDef?.meta?.qhs || {};
  const tableState = props.table?.options?.state || {};
  const isFirstVisibleColumn =
    !!meta.firstVisibleColumKey && meta.firstVisibleColumKey === meta.accessorKey;
  return (
    <Cell
      isDefaultCell={meta.isDefaultCell}
      accessorKey={meta.accessorKey}
      formattedFunction={meta.formattedFunction}
      infoPanePage={meta.infoPanePage}
      withBubble={meta.withBubble}
      withBubbles={meta.withBubbles}
      subtext={meta.subtextKey ? props.row?.original?.[meta.subtextKey] : undefined}
      selectorOptionsFromApi={meta.selectorOptionsFromApi}
      row={props.row?.original}
      filter={tableState.filter}
      renderedCellValue={props.renderedCellValue}
      dataKey={meta.accessorKey}
      hasManageViewed={isFirstVisibleColumn ? tableState.hasManageViewed : false}
      canManageViewed={isFirstVisibleColumn ? tableState.canManageViewed : false}
      showSkeletons={tableState.canShowSkeletons}
    />
  );
}

const QhsFilterRenderer = props => {
  const filterMeta = props.column?.columnDef?.meta?.qhsFilter;
  if (!filterMeta) return null;
  return <FilterBox {...props} columnData={filterMeta} />;
};

const Cell = memo(
  ({
    formattedFunction,
    withBubble,
    withBubbles,
    row,
    canManageViewed,
    renderedCellValue,
    infoPanePage,
    hasManageViewed,
    filter,
    isDefaultCell,
    accessorKey,
    showSkeletons,
    selectorOptionsFromApi,
    subtext,
  }) => {
    const currentValue = useMemo(
      () =>
        formattedFunction
          ? formattedFunction(renderedCellValue) || '-'
          : !renderedCellValue || (Array.isArray(renderedCellValue) && !renderedCellValue.length)
          ? '-'
          : renderedCellValue,
      [renderedCellValue]
    );

    return row.showShimmer || showSkeletons ? (
      <_Shimmer />
    ) : (isDefaultCell ||
        !currentValue ||
        currentValue === '-' ||
        (Array.isArray(currentValue) && !currentValue.length)) &&
      !hasManageViewed ? (
      <CellSimpleText value={currentValue} subtext={subtext} />
    ) : (
      <CustomTableCell
        hasManageViewed={hasManageViewed}
        canManageViewed={canManageViewed}
        infoPanePage={infoPanePage}
        selectorOptionsFromApi={selectorOptionsFromApi}
        withBubble={withBubble}
        withBubbles={withBubbles}
        row={row}
        renderedCellValue={currentValue}
        dataKey={accessorKey}
        filter={filter}
      />
    );
  }
);

const SHIMMER_PREFIX = 'Shimmer';
const shimmerClasses = {
  show: `${SHIMMER_PREFIX}-show`,
};

// Background / animation are owned by the global `.qhs-shimmer`
// utility (see `tokens.css`) — that class swaps the placeholder
// colour with the dark-theme `--qhs-skeleton` token automatically
// and adds the gradient sweep, so this wrapper only sets layout.
// Was a hardcoded `#dee2e6` Bootstrap-grey that read as a glaring
// white tile on a dark surface.
const ShimmerRoot = styled('div')(() => ({
  [`&.${shimmerClasses.show}`]: {
    maxWidth: '100%',
    height: '1.25rem',
    maxHeight: 20,
    display: 'flex',
    visibility: 'visible',
  },
}));

const _Shimmer = memo(() => {
  const rndInt = randomIntFromInterval(50, 80);

  return (
    <ShimmerRoot
      style={{ minWidth: rndInt + '%', height: 21 }}
      className={cx(shimmerClasses.show, 'qhs-shimmer')}
    />
  );
});

export function useForceRepaintOnVisibility(selector) {
  useEffect(() => {
    const forceRepaint = () => {
      if (document.visibilityState === 'visible') {
        requestAnimationFrame(() => {
          const el = document.querySelector(selector);
          if (el) {
            el.style.display = 'none';
            void el.offsetHeight;
            el.style.display = '';
          }
        });
      }
    };

    document.addEventListener('visibilitychange', forceRepaint);
    return () => document.removeEventListener('visibilitychange', forceRepaint);
  }, [selector]);
}
